import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

const Overlay = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 0.5rem;
  border-radius: 6px;
  color: #666;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const LogoPreview = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  object-fit: contain;
  background: #f8f9fa;
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.875rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: #e9ecef;
    color: #666;

    &:hover {
      background: #dee2e6;
    }
  `}
`;

interface CompanyFormData {
  user_id?: string;
  plan_id?: string;
  name: string;
  cnpj: string;
  description?: string;
  email?: string;
  phone?: string;
  address_street?: string;
  address_number?: string;
  address_complement?: string;
  address_neighborhood?: string;
  address_city?: string;
  address_state?: string;
  address_zip_code?: string;
  status?: string;
}

interface CompanyModalProps {
  show: boolean;
  mode: 'create' | 'edit' | 'view';
  company?: any;
  users?: any[];
  plans?: any[];
  onClose: () => void;
  onSubmit: (data: CompanyFormData & { logo?: File }) => Promise<void>;
}

export const CompanyModal: React.FC<CompanyModalProps> = ({
  show,
  mode,
  company,
  users = [],
  plans = [],
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    cnpj: '',
    description: '',
    email: '',
    phone: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
    status: 'pending'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    if (company && (mode === 'edit' || mode === 'view')) {
      setFormData({
        user_id: company.user_id?.toString() || '',
        plan_id: company.plan_id?.toString() || '',
        name: company.name || '',
        cnpj: company.cnpj || '',
        description: company.description || '',
        email: company.email || '',
        phone: company.phone || '',
        address_street: company.address_street || '',
        address_number: company.address_number || '',
        address_complement: company.address_complement || '',
        address_neighborhood: company.address_neighborhood || '',
        address_city: company.address_city || '',
        address_state: company.address_state || '',
        address_zip_code: company.address_zip_code || '',
        status: company.status || 'pending'
      });
      setLogoPreview(company.logo_url || null);
      setLogoFile(null);
    } else {
      // Reset form for create mode
      setFormData({
        name: '',
        cnpj: '',
        description: '',
        email: '',
        phone: '',
        address_street: '',
        address_number: '',
        address_complement: '',
        address_neighborhood: '',
        address_city: '',
        address_state: '',
        address_zip_code: '',
        status: 'pending'
      });
      setLogoPreview(null);
      setLogoFile(null);
    }
    setErrors({});
  }, [company, mode, show]);

  // Máscaras de formatação
  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 14) {
      return numbers
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return value;
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      if (numbers.length <= 10) {
        return numbers
          .replace(/^(\d{2})(\d)/, '($1) $2')
          .replace(/(\d{4})(\d)/, '$1-$2');
      }
      return numbers
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 8) {
      return numbers.replace(/^(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const fetchAddressByCEP = async (cep: string) => {
    const cleanCEP = cep.replace(/\D/g, '');
    if (cleanCEP.length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
      const data = await response.json();

      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          address_street: data.logradouro || prev.address_street,
          address_neighborhood: data.bairro || prev.address_neighborhood,
          address_city: data.localidade || prev.address_city,
          address_state: data.uf || prev.address_state
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    
    // Aplicar máscaras
    if (name === 'cnpj') {
      formattedValue = formatCNPJ(value);
    } else if (name === 'phone') {
      formattedValue = formatPhone(value);
    } else if (name === 'address_zip_code') {
      formattedValue = formatCEP(value);
      // Buscar endereço quando CEP estiver completo
      const cleanCEP = value.replace(/\D/g, '');
      if (cleanCEP.length === 8) {
        fetchAddressByCEP(value);
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);

    if (!file) {
      setLogoPreview(company?.logo_url || null);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.cnpj.trim()) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    } else if (formData.cnpj.replace(/\D/g, '').length !== 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'view') {
      onClose();
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = logoFile ? { ...formData, logo: logoFile } : { ...formData };
      await onSubmit(payload);
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isReadOnly = mode === 'view';

  const getModalTitle = () => {
    switch (mode) {
      case 'create': return 'Nova Empresa';
      case 'edit': return 'Editar Empresa';
      case 'view': return 'Detalhes da Empresa';
    }
  };

  return (
    <Overlay $show={show} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{getModalTitle()}</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {/* Informações Básicas */}
            <h3 style={{ marginTop: 0, color: '#00d4aa' }}>Informações Básicas</h3>

            <FormRow>
              <FormGroup>
                <Label>Nome da Empresa *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="Nome completo da empresa"
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>CNPJ *</Label>
                <Input
                  type="text"
                  name="cnpj"
                  value={formData.cnpj}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
                {errors.cnpj && <ErrorMessage>{errors.cnpj}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormGroup $fullWidth>
              <Label>Descrição</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={isReadOnly}
                placeholder="Descrição da empresa"
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Logo da Empresa</Label>
                <Input
                  type="file"
                  name="logo"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={handleLogoChange}
                  disabled={isReadOnly}
                />
              </FormGroup>
              <FormGroup>
                <Label>Pré-visualização</Label>
                {logoPreview ? (
                  <LogoPreview src={logoPreview} alt="Logo da empresa" />
                ) : (
                  <span style={{ color: '#999', fontSize: '0.875rem' }}>Nenhum logo enviado</span>
                )}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="contato@empresa.com"
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>Telefone</Label>
                <Input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="(00) 00000-0000"
                />
              </FormGroup>
            </FormRow>

            {/* Endereço */}
            <h3 style={{ color: '#00d4aa', marginTop: '2rem' }}>Endereço</h3>

            <FormRow>
              <FormGroup>
                <Label>CEP</Label>
                <Input
                  type="text"
                  name="address_zip_code"
                  value={formData.address_zip_code}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="00000-000"
                  maxLength={9}
                />
              </FormGroup>

              <FormGroup>
                <Label>Rua</Label>
                <Input
                  type="text"
                  name="address_street"
                  value={formData.address_street}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="Nome da rua"
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Número</Label>
                <Input
                  type="text"
                  name="address_number"
                  value={formData.address_number}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="Número"
                />
              </FormGroup>

              <FormGroup>
                <Label>Complemento</Label>
                <Input
                  type="text"
                  name="address_complement"
                  value={formData.address_complement}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="Apto, sala, etc"
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Bairro</Label>
                <Input
                  type="text"
                  name="address_neighborhood"
                  value={formData.address_neighborhood}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="Bairro"
                />
              </FormGroup>

              <FormGroup>
                <Label>Cidade</Label>
                <Input
                  type="text"
                  name="address_city"
                  value={formData.address_city}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  placeholder="Cidade"
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Estado</Label>
                <Select
                  name="address_state"
                  value={formData.address_state}
                  onChange={handleChange}
                  disabled={isReadOnly}
                >
                  <option value="">Selecione</option>
                  <option value="AC">AC</option>
                  <option value="AL">AL</option>
                  <option value="AP">AP</option>
                  <option value="AM">AM</option>
                  <option value="BA">BA</option>
                  <option value="CE">CE</option>
                  <option value="DF">DF</option>
                  <option value="ES">ES</option>
                  <option value="GO">GO</option>
                  <option value="MA">MA</option>
                  <option value="MT">MT</option>
                  <option value="MS">MS</option>
                  <option value="MG">MG</option>
                  <option value="PA">PA</option>
                  <option value="PB">PB</option>
                  <option value="PR">PR</option>
                  <option value="PE">PE</option>
                  <option value="PI">PI</option>
                  <option value="RJ">RJ</option>
                  <option value="RN">RN</option>
                  <option value="RS">RS</option>
                  <option value="RO">RO</option>
                  <option value="RR">RR</option>
                  <option value="SC">SC</option>
                  <option value="SP">SP</option>
                  <option value="SE">SE</option>
                  <option value="TO">TO</option>
                </Select>
              </FormGroup>

              {mode !== 'view' && (
                <FormGroup>
                  <Label>Status</Label>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    disabled={isReadOnly}
                  >
                    <option value="pending">Pendente</option>
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                    <option value="suspended">Suspenso</option>
                  </Select>
                </FormGroup>
              )}
            </FormRow>

            {/* Configurações */}
            {mode !== 'view' && (
              <>
                <h3 style={{ color: '#00d4aa', marginTop: '2rem' }}>Configurações</h3>

                <FormRow>
                  {users.length > 0 && (
                    <FormGroup>
                      <Label>Proprietário</Label>
                      <Select
                        name="user_id"
                        value={formData.user_id}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="">Selecione um usuário</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                  )}

                  {plans.length > 0 && (
                    <FormGroup>
                      <Label>Plano</Label>
                      <Select
                        name="plan_id"
                        value={formData.plan_id}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="">Selecione um plano</option>
                        {plans.map(plan => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} - R$ {plan.price}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                  )}
                </FormRow>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button type="button" $variant="secondary" onClick={onClose}>
              {mode === 'view' ? 'Fechar' : 'Cancelar'}
            </Button>
            {mode !== 'view' && (
              <Button type="submit" $variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : mode === 'create' ? 'Criar Empresa' : 'Salvar Alterações'}
              </Button>
            )}
          </ModalFooter>
        </Form>
      </ModalContainer>
    </Overlay>
  );
};
